import type { Metric } from '@/types'

export type SolutionVisual =
    | 'agents'
    | 'workflow'
    | 'automation'
    | 'process'
    | 'support'
    | 'sales'
    | 'operations'
    | 'knowledge'

export type Solution = {
    slug: string
    index: string
    name: string
    tagline: string
    description: string
    handles: string[]
    outcomes: Metric[]
    visual: SolutionVisual
    image: string
    imageAlt: string
}

export const SOLUTIONS: Solution[] = [
    {
        slug: 'ai-agents',
        index: '01',
        name: 'AI Agents',
        tagline: 'Digital colleagues that own entire tasks.',
        description: 'Each agent is built around one job your team does every day, such as triaging requests, reconciling records or preparing briefs. It learns your rules and your tone, works inside the tools you already use, and asks a person whenever a decision falls outside its lane.',
        handles: [
            'Request triage and routing',
            'Data entry and reconciliation',
            'Research and summarization',
            'Scheduling and coordination'
        ],
        outcomes: [
            { value: 24, suffix: '/7', label: 'Always-on capacity, with no queues' },
            { value: 70, suffix: '%', label: 'Of routine requests handled end to end' }
        ],
        visual: 'agents',
        image: '/images/arch-stairs.jpg',
        imageAlt: 'Sunlit staircase in a calm, minimal atrium'
    },
    {
        slug: 'digital-workflows',
        index: '02',
        name: 'Digital Workflows',
        tagline: 'Processes that run themselves, step by step.',
        description: 'We trace every step a process takes today, then rebuild it so information travels from tool to tool on its own. Approvals reach the right person at the right moment, and nothing sits waiting in an inbox.',
        handles: [
            'Approvals and sign-offs',
            'Cross-system data sync',
            'Onboarding and offboarding',
            'Exception handling'
        ],
        outcomes: [
            { value: 10, suffix: '×', label: 'Faster cycle times on approvals' },
            { value: 0, label: 'Copy and paste between tools' }
        ],
        visual: 'workflow',
        image: '/images/arch-curve.jpg',
        imageAlt: 'Curved modern facade with arched doorways'
    },
    {
        slug: 'ai-automation',
        index: '03',
        name: 'AI Automation',
        tagline: 'Automation that reads, reasons and adapts.',
        description: 'Classic automation follows rules. AI automation understands unstructured inputs like emails, documents and conversations, so it keeps working when real life does not fit the template.',
        handles: [
            'Email and document understanding',
            'Classification and extraction',
            'Content generation with review',
            'Anomaly detection'
        ],
        outcomes: [
            { value: 98, suffix: '%', label: 'Extraction accuracy on live documents' },
            { value: 85, suffix: '%', label: 'Less manual review' }
        ],
        visual: 'automation',
        image: '/images/desert-house.jpg',
        imageAlt: 'Sculptural white house in the desert at dusk'
    },
    {
        slug: 'business-process-automation',
        index: '04',
        name: 'Business Process Automation',
        tagline: 'Core processes, rebuilt around automation.',
        description: 'We redesign end-to-end processes such as order to cash, procure to pay and claims handling, removing hand-offs and waiting time instead of simply speeding up broken steps.',
        handles: [
            'Order to cash',
            'Procure to pay',
            'Claims and case handling',
            'Compliance checks'
        ],
        outcomes: [
            { value: 60, suffix: '%', label: 'Lower cost per process' },
            { value: 4, suffix: ' min', label: 'Average end-to-end cycle' }
        ],
        visual: 'process',
        image: '/images/case-finance.jpg',
        imageAlt: 'Towers rising above low clouds over a city'
    },
    {
        slug: 'customer-support-automation',
        index: '05',
        name: 'Customer Support Automation',
        tagline: 'Instant, accurate answers on every channel.',
        description: 'AI agents resolve common requests across chat, email and voice, draft replies for complex cases and hand over to your team with the full context, so customers never have to repeat themselves.',
        handles: [
            'Ticket triage and tagging',
            'Order status and account changes',
            'Refunds and returns',
            'Agent assist and reply drafting'
        ],
        outcomes: [
            { value: 68, suffix: '%', label: 'Of tickets resolved automatically' },
            { value: 40, suffix: 's', label: 'Average first response' }
        ],
        visual: 'support',
        image: '/images/case-healthcare.jpg',
        imageAlt: 'Quiet, sunlit waiting corridor with soft coral walls'
    },
    {
        slug: 'sales-automation',
        index: '06',
        name: 'Sales Automation',
        tagline: 'More pipeline, less admin.',
        description: 'From lead research and scoring to personalized follow-up and CRM hygiene, Cortex automates the work around selling so your team spends its time in conversations that close.',
        handles: [
            'Lead enrichment and scoring',
            'Personalized outreach drafts',
            'Meeting notes to CRM',
            'Pipeline forecasting'
        ],
        outcomes: [
            { value: 3.4, decimals: 1, suffix: '×', label: 'More qualified pipeline' },
            { value: 9, suffix: ' hrs', label: 'Saved per rep, every week' }
        ],
        visual: 'sales',
        image: '/images/strategy-workshop.jpg',
        imageAlt: 'Minimal workshop room with a whiteboard and stools'
    },
    {
        slug: 'internal-operations',
        index: '07',
        name: 'Internal Operations',
        tagline: 'Finance, HR and IT on autopilot.',
        description: 'Automate the back office: invoice processing, expense checks, employee requests, access management and reporting, with controls your auditors will appreciate.',
        handles: [
            'Invoice and expense processing',
            'HR and IT service requests',
            'Access and permissions',
            'Scheduled reporting'
        ],
        outcomes: [
            { value: 87, suffix: '%', label: 'Faster invoice processing' },
            { value: 100, suffix: '%', label: 'Of actions with a full audit trail' }
        ],
        visual: 'operations',
        image: '/images/case-logistics.jpg',
        imageAlt: 'Cargo ship emerging from morning fog'
    },
    {
        slug: 'research-knowledge-systems',
        index: '08',
        name: 'Research & Knowledge Systems',
        tagline: 'Your organization’s memory, searchable and cited.',
        description: 'We connect your documents, wikis, tickets and data into a secure knowledge system that answers questions with sources, drafts research and keeps institutional knowledge from walking out the door.',
        handles: [
            'Cited answers from internal documents',
            'Market and competitor research',
            'Policy and contract lookup',
            'Onboarding assistants'
        ],
        outcomes: [
            { value: 5, suffix: 's', label: 'To a cited answer' },
            { value: 45, suffix: '%', label: 'Less time spent searching' }
        ],
        visual: 'knowledge',
        image: '/images/cortex-meadow.png',
        imageAlt: 'Bicycle with a flower basket on a sunny meadow hill'
    }
]
